export function escapeHtml(value) {
    if (value === null || value === undefined) return '';
    return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

export function debounce(fn, wait) {
    let timer = null;
    return function debounced(...args) {
        clearTimeout(timer);
        timer = setTimeout(() => fn.apply(this, args), wait);
    };
}

export function throttle(fn, wait) {
    let last = 0;
    let timer = null;
    return function throttled(...args) {
        const now = Date.now();
        const remaining = wait - (now - last);
        if (remaining <= 0) {
            clearTimeout(timer);
            timer = null;
            last = now;
            fn.apply(this, args);
        } else if (!timer) {
            timer = setTimeout(() => {
                last = Date.now();
                timer = null;
                fn.apply(this, args);
            }, remaining);
        }
    };
}

export function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
}

export function pad2(n) {
    return n < 10 ? '0' + n : String(n);
}

export function formatClock(ts) {
    if (!ts) return '';
    const d = ts instanceof Date ? ts : new Date(ts);
    if (isNaN(d.getTime())) return '';
    return pad2(d.getHours()) + ':' + pad2(d.getMinutes());
}

export function formatDuration(seconds) {
    if (!isFinite(seconds) || seconds < 0) return '0:00';
    const s = Math.floor(seconds);
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    if (h > 0) return h + ':' + pad2(m) + ':' + pad2(sec);
    return m + ':' + pad2(sec);
}

export function formatMinutes(seconds) {
    if (!isFinite(seconds) || seconds <= 0) return '';
    const m = Math.round(seconds / 60);
    if (m < 60) return m + ' min';
    const h = Math.floor(m / 60);
    const rest = m % 60;
    return rest ? h + ' h ' + rest + ' min' : h + ' h';
}

export function formatDate(ts, lang) {
    if (!ts) return '';
    const d = ts instanceof Date ? ts : new Date(typeof ts === 'number' && ts < 1e12 ? ts * 1000 : ts);
    if (isNaN(d.getTime())) return '';
    try {
        return d.toLocaleDateString(lang || undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    } catch (e) {
        return d.toDateString();
    }
}

export function formatBytes(bytes) {
    if (!bytes) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
    return (bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1) + ' ' + units[i];
}

const COUNTRY_PATTERNS = [
    /^\|?\s*([A-Z]{2})\s*\|\s*/i,
    /^\[([A-Z]{2})\]\s*/i,
    /^\(([A-Z]{2})\)\s*/i,
    /^([A-Z]{2})\s*[-:]\s+/
];

export function extractCountry(name) {
    if (!name) return null;
    for (const re of COUNTRY_PATTERNS) {
        const m = name.match(re);
        if (m) return m[1].toUpperCase();
    }
    return null;
}

export function cleanName(name) {
    if (!name) return '';
    let out = String(name);
    for (const re of COUNTRY_PATTERNS) out = out.replace(re, '');
    return out.replace(/\s{2,}/g, ' ').trim();
}

export function qualityOf(name) {
    const n = (name || '').toUpperCase();
    if (/\b(UHD|4K|2160P?)\b/.test(n)) return 'UHD';
    if (/\b(FHD|FULL\s*HD|1080P?)\b/.test(n)) return 'FHD';
    if (/\b(HD|720P?)\b/.test(n)) return 'HD';
    if (/\b(SD|480P?|576P?)\b/.test(n)) return 'SD';
    return null;
}

/** Resolución en líneas que anuncia el nombre del canal («ABN Africa (480p)», «Canal 576i»), o 0 si no la dice. */
export function resolutionOf(name) {
    const m = /(?:^|[^0-9a-z])(2160|1440|1080|720|576|480|360|240)\s*[pi](?![a-z0-9])/i.exec(String(name || ''));
    return m ? Number(m[1]) : 0;
}

export function normalizeText(value) {
    return String(value || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/[^a-z0-9\s]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

export function uid() {
    return Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
}

export function toNumber(value, fallback = 0) {
    const n = typeof value === 'number' ? value : parseFloat(value);
    return isFinite(n) ? n : fallback;
}

export function pick(obj, keys) {
    const out = {};
    keys.forEach((k) => { if (obj[k] !== undefined) out[k] = obj[k]; });
    return out;
}

export function shuffle(arr) {
    const a = arr.slice();
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
}

export function once(fn) {
    let done = false;
    let result;
    return function () {
        if (!done) {
            done = true;
            result = fn.apply(this, arguments);
        }
        return result;
    };
}

export async function sha256(text) {
    if (window.crypto && window.crypto.subtle) {
        const data = new TextEncoder().encode(text);
        const hash = await window.crypto.subtle.digest('SHA-256', data);
        return Array.from(new Uint8Array(hash)).map((b) => b.toString(16).padStart(2, '0')).join('');
    }
    // Fallback (contextos no seguros como http:// en WebViews antiguos): hash FNV-1a, suficiente para un PIN local.
    let h = 0x811c9dc5;
    for (let i = 0; i < text.length; i++) {
        h ^= text.charCodeAt(i);
        h = Math.imul(h, 0x01000193) >>> 0;
    }
    return 'fnv:' + h.toString(16);
}

export function decodeEntities(text) {
    if (!text || text.indexOf('&') === -1) return text || '';
    return text
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&apos;/g, "'")
        .replace(/&#39;/g, "'")
        .replace(/&#(\d+);/g, (m, code) => String.fromCharCode(parseInt(code, 10)))
        .replace(/&#x([0-9a-f]+);/gi, (m, code) => String.fromCharCode(parseInt(code, 16)))
        .replace(/&amp;/g, '&');
}

/**
 * Las imágenes de TMDB se guardan en el tamaño grande (póster w500, fondo w1280) y se piden más pequeñas donde no
 * hace falta: las tarjetas muestran el póster a ~150-250 px (w342 sobra) y en pantallas estrechas el fondo cabe en
 * w780. Mismo dibujo, hasta la mitad de bytes. Cualquier otra dirección se devuelve igual.
 */
export function tmdbSized(src, { card = false, narrow = false } = {}) {
    const s = String(src || '');
    if (s.indexOf('image.tmdb.org/t/p/') === -1) return s;
    if (card) return s.replace('/t/p/w500/', '/t/p/w342/');
    if (narrow) return s.replace('/t/p/w1280/', '/t/p/w780/');
    return s;
}

export class Emitter {
    constructor() {
        this._handlers = {};
    }

    on(event, handler) {
        (this._handlers[event] = this._handlers[event] || []).push(handler);
        return () => this.off(event, handler);
    }

    off(event, handler) {
        const list = this._handlers[event];
        if (!list) return;
        const i = list.indexOf(handler);
        if (i >= 0) list.splice(i, 1);
    }

    emit(event, ...args) {
        const list = this._handlers[event];
        if (!list) return;
        list.slice().forEach((h) => {
            try {
                h(...args);
            } catch (err) {
                console.error('[emitter] handler error for ' + event, err);
            }
        });
    }
}

export function isHlsUrl(url) {
    return /\.m3u8?(\?|#|$)/i.test(url) || /\/live\//i.test(url) && !/\.(ts|mp4|mkv)(\?|#|$)/i.test(url);
}

export function extOf(url) {
    const m = String(url || '').split(/[?#]/)[0].match(/\.([a-z0-9]{2,5})$/i);
    return m ? m[1].toLowerCase() : '';
}

/**
 * Identificador opaco y estable (32 hex) a partir de una cadena: cuatro FNV-1a de 32 bits con
 * semillas distintas. No es criptográfico: sirve para que las URL públicas no expongan los
 * identificadores del proveedor y no sean secuenciales.
 */
export function opaqueId(input) {
    const str = String(input);
    const seeds = [0x811c9dc5, 0x9747b28c, 0x2f6f5c1b, 0x5bd1e995];
    let out = '';
    for (let s = 0; s < seeds.length; s++) {
        let h = seeds[s] >>> 0;
        for (let i = 0; i < str.length; i++) {
            h ^= str.charCodeAt(i);
            h = Math.imul(h, 0x01000193) >>> 0;
        }
        h ^= h >>> 15;
        h = Math.imul(h, 0x2c1b3c6d) >>> 0;
        h ^= h >>> 12;
        out += ('00000000' + h.toString(16)).slice(-8);
    }
    return out;
}

/** "Actor Uno, Actriz Dos" → ['Actor Uno', 'Actriz Dos'] (separadores: coma, punto y coma, barra). */
export function splitCast(cast) {
    return String(cast || '')
        .split(/[,;|/]+/)
        .map((x) => x.trim())
        .filter((x) => x.length > 1);
}
